import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_ITEMS } from "@/data/navigation";

const navTestId = (to) => to.replace(/\//g, "") || "home";

const isItemActive = (item, pathname) =>
  item.to === "/"
    ? pathname === "/"
    : pathname === item.to ||
      (item.children || []).some(
        (c) => pathname === c.to || pathname.startsWith(`${c.to}/`)
      );

const desktopTextClass = (item, active) =>
  item.featured
    ? "text-gold hover:text-[#8F6B22]"
    : active
    ? "text-graphite"
    : "text-graphite-soft hover:text-graphite";

const underlineClass = (active) =>
  active
    ? "opacity-100"
    : "opacity-0 group-hover:opacity-40";

function DesktopNavItem({ item, pathname }) {
  const active = isItemActive(item, pathname);
  const link = (
    <Link
      to={item.to}
      data-testid={`nav-link-${navTestId(item.to)}`}
      aria-current={active ? "page" : undefined}
      className={`group relative inline-flex items-center gap-1.5 font-body text-[11.5px] 2xl:text-[12.5px] tracking-[0.16em] 2xl:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap pb-1.5 ${desktopTextClass(item, active)}`}
    >
      {item.label}
      {item.children && (
        <ChevronDown
          size={11}
          strokeWidth={1.5}
          aria-hidden="true"
          className="opacity-60 transition-transform duration-300 group-hover/dd:rotate-180"
        />
      )}
      <span
        aria-hidden="true"
        className={`absolute left-0 right-0 -bottom-0.5 h-px bg-[var(--accent-gold)] transition-opacity duration-300 ${underlineClass(active)}`}
      />
    </Link>
  );

  if (!item.children) return link;

  return (
    <div className="relative group/dd flex" data-testid={`nav-group-${navTestId(item.to)}`}>
      {link}
      <div
        data-testid={`nav-dropdown-${navTestId(item.to)}`}
        className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 transition-[opacity,transform,visibility] duration-300 group-hover/dd:opacity-100 group-hover/dd:visible group-hover/dd:translate-y-0 group-focus-within/dd:opacity-100 group-focus-within/dd:visible group-focus-within/dd:translate-y-0"
      >
        <ul
          className="min-w-[250px] border border-hairline py-3"
          style={{
            backgroundColor: "#F9F8F6",
            boxShadow: "0 18px 40px -28px rgba(160, 122, 40, 0.35)",
          }}
        >
          {item.children.map((c) => (
            <li key={c.to}>
              <NavLink
                to={c.to}
                end
                data-testid={`nav-sublink-${navTestId(c.to)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 font-body text-[11.5px] tracking-[0.18em] uppercase whitespace-nowrap transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-graphite-soft hover:text-gold"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      aria-hidden="true"
                      className={`h-px bg-[var(--accent-gold)] transition-all duration-300 ${
                        isActive ? "w-4 opacity-100" : "w-2 opacity-40"
                      }`}
                    />
                    {c.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MobileNavItem({ item, pathname, onNavigate }) {
  const active = pathname === item.to;
  return (
    <li>
      <Link
        to={item.to}
        data-testid={`mobile-nav-link-${navTestId(item.to)}`}
        onClick={onNavigate}
        className={`flex items-center gap-3 py-4 font-body text-[13px] tracking-[0.22em] uppercase transition-colors duration-300 ${
          item.featured
            ? "text-gold"
            : active
            ? "text-graphite"
            : "text-graphite-soft hover:text-graphite"
        }`}
      >
        {item.label}
        {active && <span aria-hidden="true" className="h-px w-8 bg-[var(--accent-gold)]" />}
      </Link>
      {item.children && (
        <ul className="pl-4 pb-3 -mt-1 flex flex-col">
          {item.children
            .filter((c) => c.to !== item.to)
            .map((c) => {
              const subActive = pathname === c.to;
              return (
                <li key={c.to}>
                  <Link
                    to={c.to}
                    data-testid={`mobile-nav-sublink-${navTestId(c.to)}`}
                    onClick={onNavigate}
                    className={`flex items-center gap-3 py-2.5 font-body text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 ${
                      subActive ? "text-gold" : "text-muted-warm hover:text-graphite"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-px bg-[var(--accent-gold)] ${subActive ? "w-4" : "w-2 opacity-50"}`}
                    />
                    {c.label}
                  </Link>
                </li>
              );
            })}
        </ul>
      )}
    </li>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen
          ? "backdrop-blur-md bg-[rgba(249,248,246,0.95)] border-b border-hairline"
          : "bg-[rgba(249,248,246,0.55)] backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-4 md:py-5 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="header-brand"
          className="font-serif-display text-[19px] md:text-[22px] text-graphite tracking-tight shrink-0"
          onClick={closeMobile}
        >
          Szász-Tóth <span className="text-gold italic font-normal">Enikő</span>
        </Link>

        <button
          type="button"
          data-testid="mobile-nav-toggle"
          aria-label={mobileOpen ? "Menü bezárása" : "Menü megnyitása"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav-drawer"
          onClick={() => setMobileOpen((v) => !v)}
          className="min-[1400px]:hidden relative w-10 h-10 flex items-center justify-center text-graphite hover:text-gold transition-colors duration-300"
        >
          <span className="sr-only">Menü</span>
          <span className="relative block w-6 h-[14px]" aria-hidden="true">
            <span
              className={`absolute left-0 right-0 h-px bg-current transition-all duration-300 ${
                mobileOpen ? "top-[6px] rotate-45" : "top-0"
              }`}
            />
            <span
              className={`absolute left-0 right-0 top-[6px] h-px bg-current transition-opacity duration-300 ${
                mobileOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute left-0 right-0 h-px bg-current transition-all duration-300 ${
                mobileOpen ? "top-[6px] -rotate-45" : "top-[12px]"
              }`}
            />
          </span>
        </button>

        <nav
          data-testid="header-nav-links"
          className="hidden min-[1400px]:flex items-center justify-end gap-x-5 2xl:gap-x-8"
        >
          {NAV_ITEMS.map((item) => (
            <DesktopNavItem key={item.to} item={item} pathname={location.pathname} />
          ))}
        </nav>
      </div>

      <div
        id="mobile-nav-drawer"
        data-testid="mobile-nav-drawer"
        className={`min-[1400px]:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          mobileOpen
            ? "max-h-[85vh] opacity-100 border-t border-hairline"
            : "max-h-0 opacity-0"
        }`}
      >
        <nav
          className="px-5 py-4 overflow-y-auto"
          style={{ backgroundColor: "#F9F8F6", maxHeight: "85vh" }}
        >
          <ul className="flex flex-col divide-y divide-hairline">
            {NAV_ITEMS.map((item) => (
              <MobileNavItem
                key={item.to}
                item={item}
                pathname={location.pathname}
                onNavigate={closeMobile}
              />
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
}
